import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, normalizeProduct } from '@/lib/firebase';
import { ArrowRight, Package } from 'lucide-react';
import ProductImage from '@/components/ProductImage';

export default function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch from urunler collection only
        const snapshot = await getDocs(collection(db, 'urunler'));
        const data = snapshot.docs.map((doc) => normalizeProduct(doc));
        // Pick 6 diverse featured products (spread across categories)
        const categoryMap = {};
        data.forEach((p) => {
          const cat = p.category || 'Diger';
          if (!categoryMap[cat]) categoryMap[cat] = [];
          categoryMap[cat].push(p);
        });
        const featured = [];
        const cats = Object.keys(categoryMap);
        let idx = 0;
        while (featured.length < 6 && idx < 100) {
          const cat = cats[idx % cats.length];
          const items = categoryMap[cat];
          const pick = items[Math.floor(idx / cats.length)];
          if (pick && !featured.find((f) => f.id === pick.id)) {
            featured.push(pick);
          }
          idx++;
        }
        setProducts(featured);
      } catch (err) {
        console.log('Firestore urunler fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const formatPrice = (price) => {
    if (!price) return '';
    return Number(price).toLocaleString('tr-TR');
  };

  return (
    <section data-testid="featured-products-section" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10"
        >
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-dark/40">
              Koleksiyon
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark tracking-tight mt-2">
              Öne Çıkan Ürünler
            </h2>
          </div>
          <Link
            to="/urunler"
            data-testid="view-all-products-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-brand-dark/60 hover:text-brand-dark transition-colors duration-200"
          >
            Tüm Ürünler
            <ArrowRight size={16} />
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Package size={48} className="mx-auto text-brand-dark/20 mb-4" />
            <p className="text-brand-dark/40 text-lg">Ürünler yükleniyor...</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {products.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/urunler/${product.id}`} className="group block">
                  <div className="relative aspect-square rounded-2xl overflow-hidden bg-slate-50 mb-3">
                    <ProductImage
                      src={product.images[0]}
                      alt={product.name}
                      name={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-medium text-brand-dark text-sm md:text-base truncate">
                    {product.name}
                  </h3>
                  {product.price > 0 && (
                    <p className="text-sm font-semibold text-brand-dark/70 mt-0.5">
                      {formatPrice(product.price)} TL
                    </p>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
